import React from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'

// components
import {
  CSmartTable,
  CButton,
  CImage,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CSmartPagination,
  CBadge,
  CFormInput
} from '@coreui/react-pro'

// icons
import ThreeDotsIconMenu from '../assets/icons/ThreeDotsOptionsIcon.png'

// styling
import { styleButtonVars5 } from './custom-styles/buttonStyles'

// helpers
import { convertMoney } from '../helpers/format-number'

// components
import FilterByPopover from './filters/FilterByPopover'
import RowActionMenu, { RowActionItem } from './table-action/RowActionMenu'

const AppTable = ({
  projectListPage,
  invoiceListPage,
  expenseListPage,
  clientListPage,
  payrollListPage,
  assetListPage,
  employeeListPage,
  supportingLinksListPage,
  userListPage,
  companyBankAccountListPage,
  companyPolicyListPage,
  policyTypeListPage,
  letterNumberListPage,
  timeOffBalanceListPage,
  companyTimeOffListPage,
  setShowConfirmDelete,
  setShowConfirmEdit,
  setShowConfirmDuplicate,
  selectable,
  handleOnRowClick,
  isClickable = false,
  tableColumns,
  tableContent,
  contentPerPage,
  activeContentPage,
  contentCount,
  setActiveContentPage,
  searchContentFilter,
  setSearchContentFilter,
  setContentPerPage,
  columnContentSorter,
  setColumnContentSorter,
  isShowModalEdit,
  isShowModalDuplicate = false,
  isLoading,
  isShowPagination = true,
  isShowDropdownMenu = true,
  isShowButtonCopy = false,
  handleCopyToClipboard,
  isShowButtonDelete = true,
  isShowTextProcessing = false,
  isShowColumnFilter = false,
  isShowButtonDuplicate = false,
  columnFilter,
  setColumnFilter,
  filterSections = [],
  isShowFilterByPopover = false
}) => {
  const navigate = useNavigate()

  return (
    <>
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3 gap-2 flex-wrap">
        <div className="app-table-search-wrapper">
          <span className="me-2">Search:</span>
          <CFormInput
            placeholder="Search..."
            value={searchContentFilter}
            onChange={(e) => setSearchContentFilter(e?.target.value)}
            className="app-table-search"
          />
        </div>

        {isShowFilterByPopover && (
          <div 
            className="d-flex align-items-start"
            style={{
              flex: '0 0 auto',
              marginLeft: 'auto',
            }}
          >
            <FilterByPopover
              title="Filters"
              buttonLabel="Filter by"
              columnFilter={columnFilter}
              setColumnFilter={setColumnFilter}
              filterSections={filterSections}
            />
          </div>
        )}

        {
          isShowTextProcessing && (
            <div style={{ flex: '0 0 auto' }}>
              <CBadge color="warning">Processing...</CBadge>
            </div>
          )
        }
      </div>
      <CSmartTable
        columns={tableColumns}
        columnFilter={ isShowColumnFilter ? { external: true } : false}
        columnSorter={{ multiple: true, resetable: true, external: true }}
        sorterValue={columnContentSorter}
        items={tableContent}
        itemsPerPage={contentPerPage}
        itemsPerPageSelect={isShowPagination}
        onItemsPerPageChange={(itemsPerPage) => setContentPerPage(itemsPerPage)}
        tableProps={{
          className: 'app-table-content-list',
          hover: true,
          responsive: true,
          style: {
            cursor: 'pointer'
          }
        }}
        tableHeadProps={{
          className: 'app-table-header'
        }}
        pagination={isShowPagination}
        loading={isLoading}
        selectable={selectable}
        clickableRows={isClickable}
        onRowClick={(item, _, columnName) => handleOnRowClick(item, columnName)}
        onSorterChange={(value) => setColumnContentSorter(value)}
        scopedColumns={{
          name: (item) => (
            <td>
              {item?.name}
            </td>
          ),
          value: (item) => (
            <td>
              {`IDR ${convertMoney(String(item?.value))}`}
            </td>
          ),
          tax_value: (item) => (
            <td>
              {`IDR ${convertMoney(String(item?.tax_value))}`}
            </td>
          ),
          cash_inflow: (item) => (
            <td>
              {`IDR ${convertMoney(String(item?.cash_inflow))}`}
            </td>
          ),
          cash_outflow: (item) => (
            <td>
              {`IDR ${convertMoney(String(item?.cash_outflow))}`}
            </td>
          ),
          balance: (item) => (
            <td>
              {`IDR ${convertMoney(String(item?.balance))}`}
            </td>
          ),
          total_project: (item) => (
            <td>
              {`${item?.total_project > 0 ? `${item?.total_project} Projects` : 'Not available'}`}
            </td>
          ),
          date: (item) => (
            <td>
              {moment(item?.date).format('DD/MM/YYYY')}
            </td>
          ),
          register_date: (item) => (
            <td>
              {moment(item?.register_date).format('DD/MM/YYYY')}
            </td>
          ),
          start_date: (item) => (
            <td>
              {moment(item?.start_date).format('DD/MM/YYYY')}
            </td>
          ),
          end_date: (item) => (
            <td>
              {moment(item?.end_date).format('DD/MM/YYYY')}
            </td>
          ),
          due_date: (item) => (
            <td>
              {moment(item?.due_date).format('DD/MM/YYYY')}
            </td>
          ),
          expected_payment_date: (item) => (
            <td>
              {moment(item?.expected_payment_date).format('DD/MM/YYYY')}
            </td>
          ),
          tax_due_date: (item) => (
            <td>
              {moment(item?.tax_due_date).format('DD/MM/YYYY')}
            </td>
          ),
          is_paid: (item) => (
            <td>
              {item?.is_paid ? 'Yes' : 'No'}
            </td>
          ),
          bupot_required: (item) => (
            <td>
              {item?.bupot_required ? 'Yes' : 'No'}
            </td>
          ),
          faktur_required: (item) => (
            <td>
              {item?.faktur_required ? 'Yes' : 'No'}
            </td>
          ),
          payment_date: (item) => (
            <td>
              {item?.payment_date ? moment(item?.payment_date).format('DD/MM/YYYY') : '-'}
            </td>
          ),
          createdAt: (item) => (
            <td>
              {moment(item?.createdAt).format('DD/MM/YYYY')}
            </td>
          ),
          attachment_url: (item) => (
            <td>
              {
                item?.attachment_url ? (
                  <a href={item?.attachment_url} target='_blank' media='file'>
                    View
                  </a>
                )
                : (
                  <>
                    No attachment
                  </>
                )
              }
            </td>
          ),
          file_url: (item) => (
            <td>
              {
                item?.file_url ? (
                  <CButton
                    style={styleButtonVars5}
                    href={item?.file_url}
                    target='_blank'
                  >
                    View document
                  </CButton>
                )
                : (
                  <>
                    No attachment
                  </>
                )
              }
            </td>
          ),
          description: (item) => (
            <td>
              {item?.description?.length > 70 ? `${item?.description?.slice(0, 70)}...` : item?.description}
            </td>
          ),
          is_contract_base: (item) => (
            <td>
              {item?.is_contract_base ? 'Yes' : 'No'}
            </td>
          ),
          is_cash: (item) => (
            <td>
              {item?.is_cash ? 'Yes' : 'No'}
            </td>
          ),
          employee_no: (item) => (
            <td>
              {item?.employee_no ? item?.employee_no : '-'}
            </td>
          ),
          letter_date: (item) => (
            <td>
              {item?.letter_date || '-'}
            </td>
          ),
          notes: (item) => (
            <td>
              {item?.notes?.length > 50 ? `${item?.notes?.slice(0, 50)}...` : item?.notes}
            </td>
          ),
          copy: (item) => (
            <td>
              {
                isShowButtonCopy && (
                  <span 
                    onClick={() => handleCopyToClipboard(item?.letter_number)}
                    style={{
                      cursor: 'pointer',
                      fontSize: '18px',
                      color: '#0d6efd'
                    }}
                    title="Click to copy letter number"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="25px" height="25px" viewBox="0 0 256 256">
                      <path d="M48.186 92.137c0-8.392 6.49-14.89 16.264-14.89s29.827-.225 29.827-.225-.306-6.99-.306-15.88c0-8.888 7.954-14.96 17.49-14.96 9.538 0 56.786.401 61.422.401 4.636 0 8.397 1.719 13.594 5.67 5.196 3.953 13.052 10.56 16.942 14.962 3.89 4.402 5.532 6.972 5.532 10.604 0 3.633 0 76.856-.06 85.34-.059 8.485-7.877 14.757-17.134 14.881-9.257.124-29.135.124-29.135.124s.466 6.275.466 15.15-8.106 15.811-17.317 16.056c-9.21.245-71.944-.49-80.884-.245-8.94.245-16.975-6.794-16.975-15.422s.274-93.175.274-101.566zm16.734 3.946l-1.152 92.853a3.96 3.96 0 0 0 3.958 4.012l73.913.22a3.865 3.865 0 0 0 3.91-3.978l-.218-8.892a1.988 1.988 0 0 0-2.046-1.953s-21.866.64-31.767.293c-9.902-.348-16.672-6.807-16.675-15.516-.003-8.709.003-69.142.003-69.142a1.989 1.989 0 0 0-2.007-1.993l-23.871.082a4.077 4.077 0 0 0-4.048 4.014zm106.508-35.258c-1.666-1.45-3.016-.84-3.016 1.372v17.255c0 1.106.894 2.007 1.997 2.013l20.868.101c2.204.011 2.641-1.156.976-2.606l-20.825-18.135zm-57.606.847a2.002 2.002 0 0 0-2.02 1.988l-.626 96.291a2.968 2.968 0 0 0 2.978 2.997l75.2-.186a2.054 2.054 0 0 0 2.044-2.012l1.268-62.421a1.951 1.951 0 0 0-1.96-2.004s-26.172.042-30.783.042c-4.611 0-7.535-2.222-7.535-6.482S152.3 63.92 152.3 63.92a2.033 2.033 0 0 0-2.015-2.018l-36.464-.23z" stroke="#979797" fill-rule="evenodd"/>
                    </svg>
                  </span>
                )
              }
            </td>
          ),
          status_invoice: (item) => (
            <td>
              <CBadge color={item?.status_invoice === 'Draft' ? 'secondary' : 'success'}>
                {item?.status_invoice}
              </CBadge>
            </td>
          ),
          invoice_payment: (item) => (
            <td>
              <CBadge color={item?.invoice_payment === 'Not Paid' ? 'secondary' : 'success'}>
                {item?.invoice_payment}
              </CBadge>
            </td>
          ),
          action: (item, index) => (
            <td>
              <CDropdown>
                {
                  isShowDropdownMenu && (
                    <>
                      <CDropdownToggle
                        color="white"
                        caret={false}
                        style={{
                          border: "none",
                          boxShadow: "none",
                          background: "#FFF",
                          borderBottomRightRadius: ".3rem",
                          borderTopRightRadius: ".3rem",
                        }}
                        split
                      >
                        <CImage fluid src={ThreeDotsIconMenu} />
                      </CDropdownToggle>
                      <CDropdownMenu>
                        {
                          isShowButtonDuplicate && (
                            <CDropdownItem
                              onClick={() => {
                                if (isShowModalDuplicate) {
                                  setShowConfirmDuplicate({ show: true, type: invoiceListPage ? 'invoice' : expenseListPage ? 'expense' : payrollListPage ? 'payroll' : supportingLinksListPage ? 'supporting-link' : companyBankAccountListPage ? 'company-bank-account' : '', item, index })
                                } else {
                                  let urlDuplicate = projectListPage ? `/project/duplicate/${item.id}`
                                  : !isShowModalDuplicate && invoiceListPage ? `/invoice/duplicate/${item.id}`
                                    : !isShowModalDuplicate && expenseListPage ? `/expense/duplicate/${item.id}`
                                      : clientListPage ? `/client/duplicate/${item.id}`
                                          : employeeListPage ? `/employee/duplicate/${item.id}`
                                            : assetListPage ? `/asset/duplicate/${item.id}`
                                              : userListPage ? `/user/duplicate/${item.id}`
                                                : companyPolicyListPage ? `/company-policy/duplicate/${item.id}`
                                                  : policyTypeListPage ? `/policy-type/duplicate/${item.id}`
                                                    : null

                                  navigate(urlDuplicate)
                                }
                              }}
                            >
                              Duplicate {
                                projectListPage ? 'Project'
                                  : invoiceListPage ? 'Invoice'
                                    : expenseListPage ? 'Expense'
                                      : clientListPage ? 'Client'
                                        : payrollListPage ? 'Payroll'
                                          : employeeListPage ? 'Employee'
                                            : assetListPage ? 'Asset'
                                              : userListPage ? 'User'
                                                : companyBankAccountListPage ? 'Company Bank Account'
                                                  : companyPolicyListPage ? 'Company Policy'
                                                    : policyTypeListPage ? 'Policy Type'
                                                      : ''
                              }
                            </CDropdownItem>
                          )
                        }
                        <CDropdownItem
                          onClick={() => {
                            if (isShowModalEdit) {
                              setShowConfirmEdit({ show: true, type: invoiceListPage ? 'invoice' : expenseListPage ? 'expense' : payrollListPage ? 'payroll' : supportingLinksListPage ? 'supporting-link' : companyBankAccountListPage ? 'company-bank-account' : '', item, index })
                            } else {
                              let urlEdit = projectListPage ? `/project/edit/${item.id}`
                              : !isShowModalEdit && invoiceListPage ? `/invoice/edit/${item.id}`
                                : !isShowModalEdit && expenseListPage ? `/expense/edit/${item.id}`
                                  : clientListPage ? `/client/edit/${item.id}`
                                      : employeeListPage ? `/employee/edit/${item.id}`
                                        : assetListPage ? `/asset/edit/${item.id}`
                                          : userListPage ? `/user/edit/${item.id}`
                                            : companyPolicyListPage ? `/company-policy/edit/${item.id}`
                                              : policyTypeListPage ? `/policy-type/edit/${item.id}`
                                                : letterNumberListPage ? `/letter-no-tracker/edit/${item.id}`
                                                  : timeOffBalanceListPage ? `/time-off-balance/edit/${item.id}`
                                                    : companyTimeOffListPage ? `/company-time-off/edit/${item.id}`
                                                      : null

                              navigate(urlEdit)
                            }
                          }}
                        >
                          Edit {
                            projectListPage ? 'Project'
                              : invoiceListPage ? 'Invoice'
                                : expenseListPage ? 'Expense'
                                  : clientListPage ? 'Client'
                                    : payrollListPage ? 'Payroll'
                                      : employeeListPage ? 'Employee'
                                        : assetListPage ? 'Asset'
                                          : userListPage ? 'User'
                                            : companyBankAccountListPage ? 'Company Bank Account'
                                              : companyPolicyListPage ? 'Company Policy'
                                                : policyTypeListPage ? 'Policy Type'
                                                  : letterNumberListPage ? 'Letter Number'
                                                    : timeOffBalanceListPage ? 'Balance' 
                                                      : companyTimeOffListPage ? 'Company Time Off' 
                                                        : ''
                          }
                        </CDropdownItem>
                        {
                          isShowButtonDelete && (
                            <CDropdownItem
                              onClick={() => setShowConfirmDelete({ show: true, type: invoiceListPage ? 'invoices' : expenseListPage ? 'expenses' : payrollListPage ? 'payroll' : supportingLinksListPage ? 'supporting-link' : companyBankAccountListPage ? 'company-bank-account' : letterNumberListPage ? 'letter-number' : '' , item: item, index })}
                            >
                              Delete {
                                projectListPage ? 'Project'
                                  : invoiceListPage ? 'Invoice'
                                    : expenseListPage ? 'Expense'
                                      : clientListPage ? 'Client'
                                        : payrollListPage ? 'Payroll'
                                          : employeeListPage ? 'Employee'
                                            : assetListPage ? 'Asset'
                                              : userListPage ? 'User'
                                                : companyBankAccountListPage ? 'Company Bank Account'
                                                  : companyPolicyListPage ? 'Company Policy'
                                                    : policyTypeListPage ? 'Policy Type'
                                                      : letterNumberListPage ? 'Letter Number'
                                                        : timeOffBalanceListPage ? 'Balance' 
                                                          : companyTimeOffListPage ? 'Company Time Off' 
                                                            : ''
                              }
                            </CDropdownItem>
                          )
                        }
                      </CDropdownMenu>
                    </>
                  )
                }
              </CDropdown>
            </td>
          ),
          action: (item, index) => (
            <td>
              {isShowDropdownMenu && (
                <RowActionMenu toggle={<CImage fluid src={ThreeDotsIconMenu} />}>
                  {isShowButtonDuplicate && (
                    <RowActionItem
                      onClick={() => {
                        if (isShowModalDuplicate) {
                          setShowConfirmDuplicate({ show: true, type: invoiceListPage ? 'invoice' : expenseListPage ? 'expense' : payrollListPage ? 'payroll' : supportingLinksListPage ? 'supporting-link' : companyBankAccountListPage ? 'company-bank-account' : '', item, index })
                        } else {
                          let urlDuplicate = projectListPage ? `/project/duplicate/${item.id}`
                          : !isShowModalDuplicate && invoiceListPage ? `/invoice/duplicate/${item.id}`
                            : !isShowModalDuplicate && expenseListPage ? `/expense/duplicate/${item.id}`
                              : clientListPage ? `/client/duplicate/${item.id}`
                                  : employeeListPage ? `/employee/duplicate/${item.id}`
                                    : assetListPage ? `/asset/duplicate/${item.id}`
                                      : userListPage ? `/user/duplicate/${item.id}`
                                        : companyPolicyListPage ? `/company-policy/duplicate/${item.id}`
                                          : policyTypeListPage ? `/policy-type/duplicate/${item.id}`
                                            : null

                          navigate(urlDuplicate)
                        }
                      }}
                    >
                      Duplicate {
                        projectListPage ? 'Project'
                          : invoiceListPage ? 'Invoice'
                            : expenseListPage ? 'Expense'
                              : clientListPage ? 'Client'
                                : payrollListPage ? 'Payroll'
                                  : employeeListPage ? 'Employee'
                                    : assetListPage ? 'Asset'
                                      : userListPage ? 'User'
                                        : companyBankAccountListPage ? 'Company Bank Account'
                                          : companyPolicyListPage ? 'Company Policy'
                                            : policyTypeListPage ? 'Policy Type'
                                              : ''
                      }
                    </RowActionItem>
                  )}

                  <RowActionItem
                    onClick={() => {
                      if (isShowModalEdit) {
                        setShowConfirmEdit({ show: true, type: invoiceListPage ? 'invoice' : expenseListPage ? 'expense' : payrollListPage ? 'payroll' : supportingLinksListPage ? 'supporting-link' : companyBankAccountListPage ? 'company-bank-account' : '', item, index })
                      } else {
                        let urlEdit = projectListPage ? `/project/edit/${item.id}`
                        : !isShowModalEdit && invoiceListPage ? `/invoice/edit/${item.id}`
                          : !isShowModalEdit && expenseListPage ? `/expense/edit/${item.id}`
                            : clientListPage ? `/client/edit/${item.id}`
                                : employeeListPage ? `/employee/edit/${item.id}`
                                  : assetListPage ? `/asset/edit/${item.id}`
                                    : userListPage ? `/user/edit/${item.id}`
                                      : companyPolicyListPage ? `/company-policy/edit/${item.id}`
                                        : policyTypeListPage ? `/policy-type/edit/${item.id}`
                                          : letterNumberListPage ? `/letter-no-tracker/edit/${item.id}`
                                            : null

                        navigate(urlEdit)
                      }
                    }}
                  >
                    Edit {
                      projectListPage ? 'Project'
                        : invoiceListPage ? 'Invoice'
                          : expenseListPage ? 'Expense'
                            : clientListPage ? 'Client'
                              : payrollListPage ? 'Payroll'
                                : employeeListPage ? 'Employee'
                                  : assetListPage ? 'Asset'
                                    : userListPage ? 'User'
                                      : companyBankAccountListPage ? 'Company Bank Account'
                                        : companyPolicyListPage ? 'Company Policy'
                                          : policyTypeListPage ? 'Policy Type'
                                            : letterNumberListPage ? 'Letter Number'
                                              : ''
                    }
                  </RowActionItem>

                  {isShowButtonDelete && (
                    <RowActionItem
                      onClick={() => setShowConfirmDelete({ show: true, type: invoiceListPage ? 'invoices' : expenseListPage ? 'expenses' : payrollListPage ? 'payroll' : supportingLinksListPage ? 'supporting-link' : companyBankAccountListPage ? 'company-bank-account' : letterNumberListPage ? 'letter-number' : '' , item: item, index })}
                    >
                      Delete {
                        projectListPage ? 'Project'
                          : invoiceListPage ? 'Invoice'
                            : expenseListPage ? 'Expense'
                              : clientListPage ? 'Client'
                                : payrollListPage ? 'Payroll'
                                  : employeeListPage ? 'Employee'
                                    : assetListPage ? 'Asset'
                                      : userListPage ? 'User'
                                        : companyBankAccountListPage ? 'Company Bank Account'
                                          : companyPolicyListPage ? 'Company Policy'
                                            : policyTypeListPage ? 'Policy Type'
                                              : letterNumberListPage ? 'Letter Number'
                                                : ''
                      }
                    </RowActionItem>
                  )}
                </RowActionMenu>
              )}
            </td>
          )
        }}
      />
      {
        isShowPagination && (
          <CSmartPagination
            align="center"
            activePage={activeContentPage}
            pages={Math.ceil(contentCount / contentPerPage) || 1}
            onActivePageChange={(activePage) => setActiveContentPage(activePage)}
            className='mt-3'
          />
        )
      }
    </div>
    </>
  )
}

export default AppTable
